import React from 'react';
import { debounce } from 'lodash';

export interface TextBlockProps {
    text: string;
}

/**
 * This renders text in mega menu and shrinks container of multiline text to be just about right size so icon can be
 * displayed next to it when inside flex container. It does it's calculation by splitting text by words and rendering
 * each word inside a separate span, then we determine the smallest possible container size for the text that would fit
 * within one line.
 *
 * @return {JSX.Element}
 */
export function TextBlock({ text }: TextBlockProps): JSX.Element {
    const container = React.createRef<HTMLSpanElement>();

    React.useLayoutEffect(
        function () {
            if (container.current) updateElementWidth(container.current);
        },
        [container, text]
    );

    React.useEffect(
        function () {
            const debouncedUpdateElementWidth = debounce(updateElementWidth, 300, { trailing: true, leading: true });
            const listener = function () {
                if (container.current) debouncedUpdateElementWidth(container.current);
            };
            window.addEventListener('resize', listener, { passive: true });
            return function () {
                window.removeEventListener('resize', listener);
            };
        },
        [container, text]
    );

    return (
        <span style={{ display: 'inline-block' }} ref={container}>
            {text.split(' ').map((part, index, array) => (
                <span key={part}>
                    {part}
                    {isNotLast(index, array) ? ' ' : ''}
                </span>
            ))}
        </span>
    );
}

function updateElementWidth(element: HTMLElement) {
    element.style.width = 'auto';

    let containerWidth = element.parentElement?.getBoundingClientRect().width;
    if (!containerWidth) return;

    // decrese by icon size and margin
    // floor it to narrow container size as browser would multiline text if it would be just about enought, so we want
    // to ignore subpixel inconsistencies
    containerWidth = Math.floor(containerWidth - 32);

    let minimumElementWidth = 0;
    let calculationElementWidth = 0;
    for (const child of Array.from(element.children) as HTMLSpanElement[]) {
        const childWidth = child.getBoundingClientRect().width;
        if (Math.ceil(calculationElementWidth + childWidth) < containerWidth) {
            calculationElementWidth += childWidth;
        } else {
            calculationElementWidth = childWidth;
        }
        minimumElementWidth = Math.max(minimumElementWidth, Math.ceil(calculationElementWidth));
    }

    element.style.width = minimumElementWidth + 'px';
}

function isNotLast(index: number, array: Array<unknown>): boolean {
    return index < array.length - 1;
}
